import React, { useState, useCallback, useEffect } from 'react';
import classnames from 'classnames';
import { Link } from 'gatsby';
import { connect, ConnectedProps, useDispatch } from 'react-redux';
import { IState } from '@pvolve/sdk/src/redux/selectors';

import Selectors from 'src/state/root-selectors';
import Actions from 'src/state/root-actions';
import AccordionMenu from 'src/components/layout/header/mobile/AccordionMenu';
import { Icon } from 'src/components/shared';
import { CartItemCount } from 'src/components/shopify';
import {
    useBreakpoints,
    Breakpoints,
    Directions,
    useDeviceDetect,
} from 'src/utils/breakpoint-utils';

import * as Styles from 'src/styles/header.module.scss';
import InspirationalQuote from '../inspirational-quote';
import { useIsFTU } from 'src/components/my-plan/MyPlanContext';

const mapStateToProps = (state: IState) => ({
    user: Selectors.auth.user(state),
    entitled: Selectors.auth.entitled(state),
    loggedIn: Selectors.auth.loggedIn(state),
    shopifyUrl: Selectors.config.shopifyUrl(state),
    isFTU: Selectors.auth.isFTU(state),
});

const connector = connect(mapStateToProps, {
    logout: () => Actions.auth.logout.trigger(),
});

interface MobileHeaderProps {
    color?: string;
    hasNewNotifications?: boolean;
    hasPreview: boolean;
    scrollPos: number;
    path: any; // FIXME: Add correct type
}

const MobileHeader = ({
    color,
    scrollPos,
    hasNewNotifications = false,
    hasPreview,
    user,
    entitled,
    loggedIn,
    shopifyUrl,
    path,
}: MobileHeaderProps & ConnectedProps<typeof connector>) => {
    const dispatch = useDispatch();

    const isFTU = useIsFTU();

    const [showMenu, setShowMenu] = useState(false);
    const [promoCollapsed, setPromoCollapsed] = useState(scrollPos > 10);

    const isTabletPortraitOrUp = useBreakpoints(Breakpoints.sm, Directions.up);
    const device = useDeviceDetect();

    const isMobile = device?.type === 'mobile';

    const toggleMenu = useCallback(() => setShowMenu((prevState) => !prevState), []);

    const logout = useCallback(() => {
        dispatch(Actions.auth.logout.trigger());
    }, [dispatch]);

    const isMyPlan = !!path.match(/my-plan/gi);
    const isAccount = !!path.match(/account/gi);

    useEffect(() => {
        if (scrollPos < 10 || scrollPos > 210) {
            setPromoCollapsed(promoCollapsed ? scrollPos > 210 : scrollPos > 10);
        }
    }, [promoCollapsed, scrollPos]);

    const headerClassnames = classnames(Styles.mobile, {
        [`bgcolor-pv-${color}`]: !!color,
        [Styles.tealBackground]: isMyPlan && isFTU,
    });

    const mobileNavWrapperClassnames = classnames(Styles.mobileNavWrapper, {
        'has-preview': isTabletPortraitOrUp && hasPreview,
        'margin-bottom--large': !isAccount && !isMobile && !isFTU,
        'page-scrolled': scrollPos > 10,
        'promo-collapsed': promoCollapsed,
        'new-experience': isMyPlan && isFTU,
    });

    const mobileNavStyle = isFTU && isMyPlan ? Styles.mobileBottomNavFtu : Styles.mobileBottomNav;
    const mobileNavBottomClassnames = classnames(mobileNavStyle, { hasPreview });

    const hamburgerStyle = classnames(Styles.hamburger, {
        [Styles.hamburgerWhiteText]: isMyPlan && isFTU,
    });

    const bannerClassnames = classnames(
        Styles.banner,
        isFTU ? 'bgcolor-pv-teal' : 'bgcolor-pv-white',
        isFTU ? Styles.largeBanner : Styles.smallBanner
    );

    const myPlanClassnames = classnames('small upper', {
        [Styles.active]: !!path.match(/my-plan/),
    });

    const workoutsClassnames = classnames('small upper', {
        [Styles.active]: !!path.match(/workout|trainers/),
    });

    const seriesClassnames = classnames('small upper', {
        [Styles.active]: !!path.match(/series/),
    });

    const logoProps = {
        name: 'pv-logo',
        size: 30,
    };
    return (
        <div className={mobileNavWrapperClassnames}>
            <div className={headerClassnames}>
                {entitled ? (
                    <Link to="/my-plan">
                        <Icon {...logoProps} />
                    </Link>
                ) : (
                    <a href={`${shopifyUrl}/`}>
                        <Icon {...logoProps} />
                    </a>
                )}
                <div className={Styles.menuRight}>
                    <a className={Styles.cart} href={`${shopifyUrl}/cart`}>
                        <Icon name="pv-shopping-bag" size={21} />
                        <CartItemCount shopifyUrl={shopifyUrl} />
                    </a>
                    <button
                        aria-label="Open mobile menu"
                        className={hamburgerStyle}
                        onClick={toggleMenu}
                    >
                        {loggedIn && (
                            <p className={`mini upper ${Styles.hello}`}>
                                Hi{!!user?.given_name ? `, ${user.given_name}` : ' there!'}
                            </p>
                        )}

                        <Icon name="pv-hamburger" size={21} />
                    </button>
                </div>
            </div>

            {isMyPlan && !isFTU && (
                // ⚠️ Currently there are two separate banners! This one and the one inside of ../desktop/HeaderNav
                // ⚠️ Make updates in both locations!
                <div className={bannerClassnames}>
                    <InspirationalQuote>
                        {(quoteText) => (
                            <h1 className="huge">
                                <span className="upper bold display--block">
                                    {quoteText.headline}
                                </span>
                                <span className="accent lower"> {quoteText.subheading}</span>
                            </h1>
                        )}
                    </InspirationalQuote>
                </div>
            )}

            {!isAccount && entitled && (
                <ul className={mobileNavBottomClassnames}>
                    <li className={myPlanClassnames}>
                        <Link to="/my-plan">My Plan</Link>
                    </li>
                    <li className={workoutsClassnames}>
                        <Link to="/workouts">Workouts</Link>
                    </li>
                    <li className={seriesClassnames}>
                        <Link to="/series">Series</Link>
                    </li>
                </ul>
            )}

            {showMenu && (
                <AccordionMenu
                    toggleMenu={toggleMenu}
                    entitled={entitled}
                    loggedIn={loggedIn}
                    shopifyUrl={shopifyUrl}
                    logout={logout}
                />
            )}
        </div>
    );
};

export default connector(MobileHeader);
